import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { printDocument } from '../../../utils/printDocument';

const ReportViewer = ({ report, onExport, onDrillDown, loading = false }) => {
  const handlePrint = async () => {
    if (!report) return;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${report?.title || 'Financial Report'}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 40px; color: #000; }
            .header { margin-bottom: 30px; border-bottom: 2px solid #000; padding-bottom: 20px; }
            .header h1 { font-size: 24px; margin-bottom: 10px; }
            .header .meta { font-size: 12px; color: #666; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            thead { background-color: #f5f5f5; }
            th { padding: 12px; text-align: left; font-weight: bold; border-bottom: 2px solid #000; }
            th:not(:first-child) { text-align: right; }
            td { padding: 10px; border-bottom: 1px solid #ddd; }
            td:not(:first-child) { text-align: right; }
            .subtotal { background-color: #f9f9f9; font-weight: 600; }
            .total { background-color: #e8e8e8; font-weight: bold; border-top: 2px solid #000; }
            .indent-1 { padding-left: 30px; }
            .indent-2 { padding-left: 50px; }
            .positive { color: #16a34a; }
            .negative { color: #dc2626; }
            .notes { margin-top: 30px; padding: 15px; background-color: #f9f9f9; border-left: 4px solid #000; }
            .notes h4 { margin-bottom: 8px; font-size: 14px; }
            .notes p { font-size: 12px; color: #666; line-height: 1.5; }
            @media print {
              body { padding: 20px; }
              @page { margin: 1cm; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${report?.title || 'Financial Report'}</h1>
            <div class="meta">
              <div>Period: ${report?.period || 'N/A'}</div>
              <div>Generated: ${report?.lastUpdated || new Date()?.toLocaleString()}</div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                ${report?.columns?.map(col => `<th>${col}</th>`)?.join('') || ''}
              </tr>
            </thead>
            <tbody>
              ${report?.data?.map(row => `
                <tr class="${row?.isTotal ? 'total' : row?.isSubtotal ? 'subtotal' : ''}">
                  <td class="${row?.indent ? `indent-${row?.indent}` : ''}">${row?.account || ''}</td>
                  <td>${row?.amount || ''}</td>
                  ${row?.comparison ? `<td>${row?.comparison}</td>` : ''}
                  ${row?.variance ? `<td class="${row?.variancePositive ? 'positive' : 'negative'}">${row?.variance}</td>` : ''}
                </tr>
              `)?.join('') || ''}
            </tbody>
          </table>
          ${report?.notes ? `
            <div class="notes">
              <h4>Notes:</h4>
              <p>${report?.notes}</p>
            </div>
          ` : ''}
        </body>
      </html>
    `;
    
    try {
      await printDocument(printContent);
    } catch (error) {
      alert(error.message || 'Failed to print the report. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 md:p-12 text-center">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6 animate-pulse">
          <Icon name="FileText" size={32} color="var(--color-muted-foreground)" />
        </div>
        <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-2">
          Generating Report...
        </h3>
        <p className="caption text-muted-foreground max-w-md mx-auto">
          Please wait while we generate your financial report
        </p>
        <div className="mt-4 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 md:p-12 text-center">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
          <Icon name="FileText" size={32} color="var(--color-muted-foreground)" />
        </div>
        <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-2">
          No Report Generated
        </h3>
        <p className="caption text-muted-foreground max-w-md mx-auto">
          Select a report type and configure parameters to generate your financial report
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground mb-2">
              {report?.title}
            </h2>
            <div className="flex flex-wrap items-center gap-3 md:gap-4 caption text-muted-foreground">
              <span className="flex items-center gap-2">
                <Icon name="Calendar" size={14} />
                {report?.period}
              </span>
              <span className="flex items-center gap-2">
                <Icon name="Clock" size={14} />
                Last updated: {report?.lastUpdated}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 md:gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              iconName="FileDown"
              iconPosition="left"
            >
              PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onExport('excel')}
              iconName="FileSpreadsheet"
              iconPosition="left"
            >
              Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onExport('csv')}
              iconName="FileText"
              iconPosition="left"
            >
              CSV
            </Button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-muted">
            <tr>
              {report?.columns?.map((column, index) => (
                <th
                  key={index}
                  className={`
                    px-4 md:px-6 py-3 md:py-4 font-body font-semibold text-xs md:text-sm text-foreground
                    ${index === 0 ? 'text-left' : 'text-right'}
                  `}
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {report?.data?.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={`
                  hover:bg-muted/50 transition-smooth
                  ${row?.isTotal ? 'bg-muted/30 font-semibold' : ''}
                  ${row?.isSubtotal ? 'bg-muted/20 font-medium' : ''}
                `}
              >
                <td className={`px-4 md:px-6 py-3 md:py-4 font-body text-sm md:text-base text-foreground ${row?.indent ? `pl-${8 + row?.indent * 4}` : ''}`}>
                  {row?.account}
                </td>
                <td
                  className="px-4 md:px-6 py-3 md:py-4 font-body text-sm md:text-base text-right data-text cursor-pointer hover:text-primary transition-smooth"
                  onClick={() => !row?.isTotal && !row?.isSubtotal && onDrillDown(row)}
                >
                  {row?.amount}
                </td>
                {row?.comparison && (
                  <td className="px-4 md:px-6 py-3 md:py-4 font-body text-sm md:text-base text-right data-text">
                    {row?.comparison}
                  </td>
                )}
                {row?.variance && (
                  <td className={`px-4 md:px-6 py-3 md:py-4 font-body text-sm md:text-base text-right data-text ${row?.variancePositive ? 'text-success' : 'text-error'}`}>
                    {row?.variance}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {report?.notes && (
        <div className="p-4 md:p-6 border-t border-border bg-muted/30">
          <h4 className="font-body font-semibold text-sm md:text-base text-foreground mb-2">
            Notes:
          </h4>
          <p className="caption text-muted-foreground">
            {report?.notes}
          </p>
        </div>
      )}
    </div>
  );
};

export default ReportViewer;
